const ALLOWED_STYLE_PROPERTIES = [
  "background-color",
  "border",
  "border-bottom",
  "border-bottom-color",
  "border-bottom-style",
  "border-bottom-width",
  "border-color",
  "border-left",
  "border-left-color",
  "border-left-style",
  "border-left-width",
  "border-right",
  "border-right-color",
  "border-right-style",
  "border-right-width",
  "border-style",
  "border-top",
  "border-top-color",
  "border-top-style",
  "border-top-width",
  "border-width",
  "color",
  "column-count",
  "column-fill",
  "column-gap",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "height",
  "line-height",
  "margin-left",
  "text-align",
  "text-decoration",
  "width",
];

const sanitizeStyle = (style = "") =>
  String(style)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .filter((rule) => {
      const [property, ...valueParts] = rule.split(":");
      const normalizedProperty = String(property || "").trim().toLowerCase();
      const value = valueParts.join(":").trim();

      return (
        ALLOWED_STYLE_PROPERTIES.includes(normalizedProperty) &&
        value &&
        !/javascript:|expression\s*\(|url\s*\(/i.test(value)
      );
    })
    .join("; ");

export const sanitizeTemplate = (template = "") =>
  String(template)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(
      /\sstyle\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi,
      (match, value) => {
        const quote = value.startsWith("'") ? "'" : '"';
        const unquotedValue = value.replace(/^['"]|['"]$/g, "");
        const safeStyle = sanitizeStyle(unquotedValue);

        return safeStyle ? ` style=${quote}${safeStyle}${quote}` : "";
      }
    )
    .replace(/javascript:/gi, "");

export const getByPath = (source, path) =>
  String(path)
    .split(".")
    .reduce((total, key) => (total ? total[key] : undefined), source);

const normalizeTemplateVariablePath = (path) =>
  decodeTemplateEntities(String(path || ""))
    .replace(/<[^>]*>/g, "")
    .trim();

const decodeTemplateEntities = (value = "") =>
  String(value)
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

export const interpolateTemplate = (template = "", context = {}) =>
  sanitizeTemplate(template).replace(/\{\{([\s\S]*?)\}\}/g, (_, path) => {
    path = normalizeTemplateVariablePath(path);

    if (!/^[\w.]+$/.test(path)) return "";

    const value = getByPath(context, path);

    if (value === null || typeof value === "undefined") return "";
    if (typeof value === "object") return "";

    return String(value);
  });

export const getPartnerName = (partner) =>
  partner?.personal?.name ||
  partner?.legal?.social_name ||
  partner?.legal?.company_name ||
  partner?.name ||
  "";

export const LABEL_PRINT_CONTEXTS = {
  PRODUCT: "product",
  ORDER_PRODUCT: "order_product",
};

export const LABEL_STORAGE_TYPES = {
  CHILLED: "chilled",
  FROZEN: "frozen",
};

export const LABEL_STORAGE_OPTIONS = [
  { label: "RESFRIADO", value: LABEL_STORAGE_TYPES.CHILLED },
  { label: "CONGELADO", value: LABEL_STORAGE_TYPES.FROZEN },
];

const dateFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

export const buildTemplateContext = ({
  label,
  order,
  product,
  printContext = LABEL_PRINT_CONTEXTS.PRODUCT,
  storageType = LABEL_STORAGE_TYPES.CHILLED,
  metadata = {},
}) => ({
  label: normalizeLabel(label, metadata, storageType),
  order:
    printContext === LABEL_PRINT_CONTEXTS.ORDER_PRODUCT
      ? normalizeOrder(order, metadata, storageType)
      : {},
  product:
    printContext === LABEL_PRINT_CONTEXTS.PRODUCT
      ? normalizeProduct(product, storageType, metadata)
      : {},
});

const normalizeLabel = (label, metadata = {}, storageType) => ({
  ...(label || {}),
  ...normalizePrintMetadata(metadata, storageType),
  expiration_days_count: label?.expiration_days,
  expiration_days: getExpirationDate(
    label?.expiration_days,
    metadata?.manufactured_at
  ),
  expiration_date: getExpirationDate(
    label?.expiration_days,
    metadata?.manufactured_at
  ),
});

const getExpirationDate = (expirationDays, manufacturedAt) => {
  if (expirationDays === null || typeof expirationDays === "undefined")
    return "";

  const days = Number(expirationDays);
  if (!Number.isFinite(days)) return "";

  const date = parseTemplateDate(manufacturedAt) || new Date();
  date.setDate(date.getDate() + days);

  return dateFormatter.format(date);
};

const parseTemplateDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime()))
    return new Date(value);

  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) return parsedDate;

  const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!match) return null;

  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
};

const normalizeOrder = (order, metadata = {}, storageType) => ({
  ...(order || {}),
  ...normalizePrintMetadata(metadata, storageType),
  products: (order?.products || []).map((product) =>
    normalizeOrderProduct(product, storageType, metadata)
  ),
  customer: {
    ...(order?.client || {}),
    name: getPartnerName(order?.client),
  },
  client_name: getPartnerName(order?.client),
  printed: {
    ...(order?.printed || {}),
  },
  printed_by:
    metadata?.printed_by || order?.printed?.name || order?.printed_by || "",
  printed_by_name:
    metadata?.printed_by || order?.printed?.name || order?.printed_by || "",
  printed_at: metadata?.printed_at || order?.printed_at || "",
  manufactured_at: metadata?.manufactured_at || "",
  production_id: metadata?.production_id || order?.production_id || "",
});

const normalizeOrderProduct = (product = {}, storageType, metadata = {}) => ({
  ...product,
  ...normalizePrintMetadata(metadata, storageType),
  weight: formatWeight(metadata?.weight || product?.weight),
  weight_raw: metadata?.weight || product?.weight || "",
  mounted: normalizeStorageTechnicalFields(product?.mounted, storageType),
});

const normalizeProduct = (product, storageType, metadata = {}) => {
  const baseProduct = product?.base_product || product?.product || product;
  const normalizedProduct = normalizeStorageTechnicalFields(product, storageType);

  return {
    ...normalizedProduct,
    ...normalizePrintMetadata(metadata, storageType),
    weight: formatWeight(metadata?.weight || product?.weight),
    weight_raw: metadata?.weight || product?.weight || "",
    name: product?.name || buildProductMountedName(product),
    base_name: baseProduct?.name || "",
    category: product?.category || baseProduct?.category,
    category_name: product?.category?.name || baseProduct?.category?.name || "",
    properties: product?.properties || baseProduct?.properties || [],
    product: baseProduct,
  };
};

const normalizePrintMetadata = (metadata = {}, storageType) => {
  const normalizedStorageType = normalizeStorageType(storageType);

  return {
    printed_by: metadata?.printed_by || "",
    printed_by_name: metadata?.printed_by || "",
    printed_at: metadata?.printed_at || "",
    manufactured_at: metadata?.manufactured_at || "",
    production_id: metadata?.production_id || "",
    copies: metadata?.copies || "",
    weight: formatWeight(metadata?.weight),
    weight_raw: metadata?.weight || "",
    storage_type: normalizedStorageType,
    storage_label:
      normalizedStorageType === LABEL_STORAGE_TYPES.FROZEN
        ? "CONGELADO"
        : "RESFRIADO",
  };
};

const weightFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const formatWeight = (value) => {
  if (value === null || typeof value === "undefined" || value === "") return "";

  const normalizedValue =
    typeof value === "string" ? value.replace(",", ".") : value;
  const numberValue = Number(normalizedValue);

  if (!Number.isFinite(numberValue)) return value;

  return weightFormatter.format(numberValue);
};

const normalizeStorageTechnicalFields = (source = {}, storageType) => {
  const isFrozen = normalizeStorageType(storageType) === LABEL_STORAGE_TYPES.FROZEN;

  return {
    ...(source || {}),
    technical_name: isFrozen
      ? source?.technical_name_frozen ||
        source?.technical_name_chilled ||
        source?.technical_name
      : source?.technical_name_chilled || source?.technical_name,
    registration_code: isFrozen
      ? source?.registration_code_frozen ||
        source?.registration_code_chilled ||
        source?.registration_code
      : source?.registration_code_chilled || source?.registration_code,
  };
};

const normalizeStorageType = (storageType) => {
  const value = String(storageType || "").toLowerCase();

  if (value === LABEL_STORAGE_TYPES.FROZEN || value.includes("congel"))
    return LABEL_STORAGE_TYPES.FROZEN;

  return LABEL_STORAGE_TYPES.CHILLED;
};

export const buildProductMountedName = (mounted = {}) =>
  [
    mounted?.base_product?.name || mounted?.product?.name || mounted?.name,
    mounted?.cutting,
    mounted?.composition,
    mounted?.storage,
  ]
    .filter(Boolean)
    .join(" ");

export const buildMountedProducts = (products = [], onlyNormalSell = false) =>
  products
    ?.map((product) =>
      (product?.mounted || [])
        .map((mounted) => {
          const mountedProduct = {
            ...mounted,
            ...(mounted?.codes || {}),
            uid: mounted.id,
            productUid: product.uid,
            base_product: product,
            product,
            price: mounted?.price,
            codes: mounted?.codes,
            category: product?.category,
            properties: product?.properties || [],
            un: product?.un,
            type: product?.type,
            min_price: product?.min_price,
            weight_percent: product?.weight_percent,
            is_active: product?.is_active,
            is_normal_sell_available: mounted?.is_normal_sell_available,
            is_public_sell_available: mounted?.is_public_sell_available,
            technical_name_chilled:
              mounted?.technical_name_chilled || mounted?.technical_name,
            registration_code_chilled:
              mounted?.registration_code_chilled || mounted?.registration_code,
            technical_name_frozen: mounted?.technical_name_frozen,
            registration_code_frozen: mounted?.registration_code_frozen,
          };

          return {
            ...mountedProduct,
            name: buildProductMountedName(mountedProduct),
          };
        })
        .filter(
          ({ is_normal_sell_available }) =>
            !onlyNormalSell || !!is_normal_sell_available
        )
    )
    ?.flat() || [];

export const LABEL_VARIABLE_GROUPS = [
  {
    label: "Etiqueta",
    variables: [
      { label: "Embalagem", value: "{{ label.packing }}" },
      {
        label: "Resfriamento ou congelamento",
        value: "{{ label.temperature_type }}",
      },
      {
        label: "Validade do produto",
        value: "{{ label.expiration_days }}",
      },
      {
        label: "Dias de vencimento do produto",
        value: "{{ label.expiration_days_count }}",
      },
      { label: "Tipo de embalagem", value: "{{ label.packing_type }}" },
      { label: "Quantidade de etiquetas", value: "{{ label.copies }}" },
      { label: "Peso informado", value: "{{ label.weight }}" },
      { label: "Data de fabricação", value: "{{ label.manufactured_at }}" },
      { label: "Lote", value: "{{ label.production_id }}" },
      { label: "Impresso por", value: "{{ label.printed_by }}" },
      { label: "Impresso em", value: "{{ label.printed_at }}" },
      { label: "Armazenamento selecionado", value: "{{ label.storage_label }}" },
    ],
  },
  {
    label: "Pedido",
    variables: [
      { label: "Código do pedido", value: "{{ order.code }}" },
      { label: "Status do pedido", value: "{{ order.status }}" },
      { label: "Status formatado", value: "{{ order.status_formatted }}" },
      { label: "Próximo status", value: "{{ order.next_status }}" },
      { label: "Data de entrega", value: "{{ order.deliver_at }}" },
      { label: "Data de conferência", value: "{{ order.checked_at }}" },
      { label: "Data entregue", value: "{{ order.delivered_at }}" },
      { label: "Forma de pagamento", value: "{{ order.payment_method }}" },
      {
        label: "Forma de pagamento formatada",
        value: "{{ order.payment_method_formatted }}",
      },
      { label: "Prazo de pagamento", value: "{{ order.payment_days }}" },
      { label: "Valor total", value: "{{ order.total_amount }}" },
      { label: "Observação do pedido", value: "{{ order.obs }}" },
      { label: "Local de entrega", value: "{{ order.deliver_in }}" },
      { label: "Número da nota", value: "{{ order.invoice_id }}" },
      { label: "Impresso por", value: "{{ order.printed_by }}" },
      { label: "Impresso em", value: "{{ order.printed_at }}" },
      { label: "Data de fabricação", value: "{{ order.manufactured_at }}" },
      { label: "Lote", value: "{{ order.production_id }}" },
    ],
  },
  {
    label: "Cliente do Pedido",
    variables: [
      { label: "Nome do cliente", value: "{{ order.customer.name }}" },
      { label: "Nome fantasia", value: "{{ order.client.legal.social_name }}" },
      { label: "Razão social", value: "{{ order.client.legal.company_name }}" },
      { label: "CNPJ", value: "{{ order.client.legal.cnpj }}" },
      { label: "Nome pessoa física", value: "{{ order.client.personal.name }}" },
      { label: "CPF", value: "{{ order.client.personal.cpf_formatted }}" },
      { label: "Endereço", value: "{{ order.client.addr.street }}" },
      { label: "Número", value: "{{ order.client.addr.number }}" },
      { label: "Bairro", value: "{{ order.client.addr.district }}" },
      { label: "Cidade", value: "{{ order.client.addr.city }}" },
      { label: "Estado", value: "{{ order.client.addr.state }}" },
      { label: "CEP", value: "{{ order.client.addr.zipcode }}" },
    ],
  },
  {
    label: "Relações do Pedido",
    variables: [
      { label: "Vendedor", value: "{{ order.seller.name }}" },
      { label: "Motorista", value: "{{ order.driver.employee.name }}" },
      { label: "Veículo", value: "{{ order.car.name }}" },
      { label: "Placa do veículo", value: "{{ order.car.plate }}" },
      { label: "Usuário criador", value: "{{ order.user.name }}" },
      { label: "Status da NF", value: "{{ order.invoice.status }}" },
      { label: "Chave da NF", value: "{{ order.invoice.chave }}" },
      { label: "Primeiro pagamento - valor", value: "{{ order.payments.0.amount }}" },
      { label: "Primeiro pagamento - vencimento", value: "{{ order.payments.0.expires_at }}" },
    ],
  },
  {
    label: "Produto Selecionado",
    variables: [
      { label: "Nome do produto", value: "{{ product.name }}" },
      { label: "Nome base do produto", value: "{{ product.base_name }}" },
      { label: "Unidade", value: "{{ product.un }}" },
      { label: "Tipo", value: "{{ product.type }}" },
      { label: "Ativo", value: "{{ product.is_active }}" },
      { label: "Categoria", value: "{{ product.category.name }}" },
      { label: "Categoria", value: "{{ product.category_name }}" },
      { label: "Armazenamento", value: "{{ product.storage }}" },
      { label: "Corte", value: "{{ product.cutting }}" },
      { label: "Composição", value: "{{ product.composition }}" },
      { label: "Grupo", value: "{{ product.group }}" },
      { label: "Peso informado", value: "{{ product.weight }}" },
      { label: "Data de fabricação", value: "{{ product.manufactured_at }}" },
      { label: "Lote", value: "{{ product.production_id }}" },
      { label: "Impresso por", value: "{{ product.printed_by }}" },
      { label: "Impresso em", value: "{{ product.printed_at }}" },
      { label: "Tabela nutricional", value: "{{ product.nutritional_table }}" },
    ],
  },
  {
    label: "Nome Técnico e Código de Registro",
    variables: [
      { label: "Nome técnico", value: "{{ product.technical_name }}" },
      { label: "Código de registro", value: "{{ product.registration_code }}" },
      {
        label: "Nome técnico resfriado",
        value: "{{ product.technical_name_chilled }}",
      },
      {
        label: "Código de registro resfriado",
        value: "{{ product.registration_code_chilled }}",
      },
      {
        label: "Nome técnico congelado",
        value: "{{ product.technical_name_frozen }}",
      },
      {
        label: "Código de registro congelado",
        value: "{{ product.registration_code_frozen }}",
      },
    ],
  },
  {
    label: "Preço e Códigos do Produto",
    variables: [
      { label: "Preço macho mínimo", value: "{{ product.price.male_min }}" },
      { label: "Preço macho ideal", value: "{{ product.price.male_std }}" },
      { label: "Preço fêmea mínimo", value: "{{ product.price.female_min }}" },
      { label: "Preço fêmea ideal", value: "{{ product.price.female_std }}" },
      { label: "Código EAN", value: "{{ product.codes.ean }}" },
      { label: "Código NCM", value: "{{ product.codes.ncm }}" },
      { label: "Código CEST", value: "{{ product.codes.cest }}" },
      { label: "Código GTIN", value: "{{ product.codes.gtin }}" },
    ],
  },
  {
    label: "Produto no Pedido",
    variables: [
      { label: "Produto do pedido", value: "{{ order.products.0.product.name }}" },
      { label: "Armazenamento", value: "{{ order.products.0.storage }}" },
      { label: "Corte", value: "{{ order.products.0.cutting }}" },
      { label: "Composição", value: "{{ order.products.0.composition }}" },
      { label: "Quantidade", value: "{{ order.products.0.quantity }}" },
      { label: "Unidade da quantidade", value: "{{ order.products.0.un_quantity }}" },
      { label: "Peso", value: "{{ order.products.0.weight }}" },
      {
        label: "Data de fabricação",
        value: "{{ order.products.0.manufactured_at }}",
      },
      { label: "Lote", value: "{{ order.products.0.production_id }}" },
      { label: "Impresso por", value: "{{ order.products.0.printed_by }}" },
      { label: "Impresso em", value: "{{ order.products.0.printed_at }}" },
      { label: "Valor unitário", value: "{{ order.products.0.amount }}" },
      { label: "Valor total do produto", value: "{{ order.products.0.total_amount }}" },
      { label: "Observação", value: "{{ order.products.0.obs }}" },
      { label: "NCM", value: "{{ order.products.0.ncm }}" },
      { label: "CEST", value: "{{ order.products.0.cest }}" },
      { label: "EAN", value: "{{ order.products.0.ean }}" },
      { label: "QR Code", value: "{{ order.products.0.qrcode }}" },
      {
        label: "Tabela nutricional",
        value: "{{ order.products.0.mounted.nutritional_table }}",
      },
    ],
  },
  {
    label: "Nome Técnico e Código de Registro do Pedido",
    variables: [
      {
        label: "Nome técnico",
        value: "{{ order.products.0.mounted.technical_name }}",
      },
      {
        label: "Código de registro",
        value: "{{ order.products.0.mounted.registration_code }}",
      },
      {
        label: "Nome técnico resfriado",
        value: "{{ order.products.0.mounted.technical_name_chilled }}",
      },
      {
        label: "Código de registro resfriado",
        value: "{{ order.products.0.mounted.registration_code_chilled }}",
      },
      {
        label: "Nome técnico congelado",
        value: "{{ order.products.0.mounted.technical_name_frozen }}",
      },
      {
        label: "Código de registro congelado",
        value: "{{ order.products.0.mounted.registration_code_frozen }}",
      },
    ],
  },
];

export const getLabelVariableGroups = (
  printContext = LABEL_PRINT_CONTEXTS.PRODUCT
) => {
  const commonGroups = ["Etiqueta"];
  const productGroups = [
    ...commonGroups,
    "Produto Selecionado",
    "Nome Técnico e Código de Registro",
    "Preço e Códigos do Produto",
  ];
  const orderProductGroups = [
    ...commonGroups,
    "Pedido",
    "Cliente do Pedido",
    "Relações do Pedido",
    "Produto no Pedido",
    "Nome Técnico e Código de Registro do Pedido",
  ];
  const allowedGroups =
    printContext === LABEL_PRINT_CONTEXTS.ORDER_PRODUCT
      ? orderProductGroups
      : productGroups;

  return LABEL_VARIABLE_GROUPS.filter((group) =>
    allowedGroups.includes(group.label)
  );
};
